import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

export default function VerifyEmail(): JSX.Element {
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'manual'>('loading');
  const [message, setMessage] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const token = query.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('manual');
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
      } catch (error: any) {
        setStatus('error');
        setMessage(error.response?.data?.error || 'Verification failed. The link may be expired.');
      }
    };

    verifyToken();
  }, [token]);

  const handleManualVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCode || manualCode.length < 6) return;

    setIsVerifying(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/verify-email?code=${manualCode}`);
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
    } catch (error: any) {
      setStatus('error');
      setMessage(error.response?.data?.error || 'Invalid or expired code.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border text-foreground shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center border border-primary/20">
              <Mail className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-3xl font-black tracking-tight">Email Verification</CardTitle>
        </CardHeader>
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <div className="space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="text-muted-foreground">Verifying your email address...</p>
            </div>
          )}

          {status === 'manual' && (
            <form onSubmit={handleManualVerify} className="space-y-6">
              <p className="text-muted-foreground text-sm">
                Enter the 6-digit code we sent to your email address.
              </p>
              <div className="flex justify-center">
                <input
                  type="text"
                  maxLength={6}
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full max-w-[260px] text-center text-3xl font-black tracking-[0.5em] pl-[0.5em] bg-background border-border text-primary rounded-xl h-16 focus:ring-2 focus:ring-primary outline-none transition-all"
                  placeholder="000000"
                />
              </div>
              <Button 
                type="submit" 
                disabled={isVerifying || manualCode.length < 6}
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 rounded-xl"
              >
                {isVerifying ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify Account'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Check your inbox (including spam) for the code.
              </p>
            </form>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <CheckCircle2 className="h-16 w-16 text-green-500" />
              </div>
              <p className="text-foreground font-medium text-lg">{message}</p>
              <Button asChild className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 rounded-xl">
                <Link to="/login">Go to Login</Link>
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <XCircle className="h-16 w-16 text-red-500" />
              </div>
              <p className="text-red-400 font-medium text-lg">{message}</p>
              <div className="space-y-3">
                <Button asChild variant="outline" className="w-full border-border text-foreground hover:bg-card font-bold h-12 rounded-xl">
                  <Link to="/login">Back to Login</Link>
                </Button>
                <p className="text-xs text-muted-foreground">
                  If you need a new link, try to login and we'll send you another one.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
